import { IconProp } from 'models/Icon';
import {
  DataRoomNodeWithStateAndSignal,
  DATA_ROOM_NODE_ACCESS_STATES,
  DATA_ROOM_NODE_ACCESS_LEVELS,
  DataRoomNodeIdWithStateAndSignal,
  DataRoomNodeAccessLevel,
  Lead,
} from 'models/Lead';
import { FILE_REORDER_VIEW_MODE, FileReorderViewMode } from 'models/User';
import { ACCESS_LEVEL_ICON } from './components/DisplayFilesByLead/data';
import { getArrayLength, getHasElementsInArray } from 'utils';
import { isDateFromLastDays } from 'dateUtils';

const MIN_USERS_FOR_LEAD = 5;
const MIN_FILES_FOR_LEAD = 10;

export const sortReleasedFiles = (nodes: DataRoomNodeWithStateAndSignal[]) =>
  nodes.sort((a, b) => {
    const aState = getDataRoomNodeAccessDisplayState(a);
    const bState = getDataRoomNodeAccessDisplayState(b);

    // 1) Files with softSignal go first, ordered by most recent softSignalAt
    const aHasSoftSignalAt = a.softSignal && !!a.softSignalAt;
    const bHasSoftSignalAt = b.softSignalAt && !!b.softSignalAt;

    if (aHasSoftSignalAt && bHasSoftSignalAt) {
      return (
        new Date(b.softSignalAt).getTime() - new Date(a.softSignalAt).getTime()
      );
    }

    // 2) Files with softSignal with no softSignalAt
    if (a.softSignal && !b.softSignal) return -1;
    if (b.softSignal && !a.softSignal) return 1;

    // 3) Then files that are available and viewed
    if (
      aState === DATA_ROOM_NODE_ACCESS_STATES.VIEWED &&
      bState !== DATA_ROOM_NODE_ACCESS_STATES.VIEWED
    )
      return -1;
    if (
      bState === DATA_ROOM_NODE_ACCESS_STATES.VIEWED &&
      aState !== DATA_ROOM_NODE_ACCESS_STATES.VIEWED
    )
      return 1;

    // 4) Then files that are available but not viewed
    if (
      aState === DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED &&
      bState !== DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED
    )
      return -1;
    if (
      bState === DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED &&
      aState !== DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED
    )
      return 1;

    // 5) Then pending files
    if (
      aState === DATA_ROOM_NODE_ACCESS_STATES.PENDING &&
      bState !== DATA_ROOM_NODE_ACCESS_STATES.PENDING
    )
      return -1;
    if (
      bState === DATA_ROOM_NODE_ACCESS_STATES.PENDING &&
      aState !== DATA_ROOM_NODE_ACCESS_STATES.PENDING
    )
      return 1;

    // 6) Then files with no access last
    if (
      aState === DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS &&
      bState !== DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS
    )
      return 1;
    if (
      bState === DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS &&
      aState !== DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS
    )
      return -1;

    // If same category, keep original order
    return 0;
  });

export const getAccessLevelIcon = ({
  accessLevel,
  state,
}: Partial<DataRoomNodeWithStateAndSignal>): IconProp => {
  if (accessLevel) return ACCESS_LEVEL_ICON[accessLevel];

  return state === DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS
    ? ACCESS_LEVEL_ICON[DATA_ROOM_NODE_ACCESS_LEVELS.NO_ACCESS]
    : ACCESS_LEVEL_ICON[DATA_ROOM_NODE_ACCESS_LEVELS.VIEW];
};

export const getDataRoomNodeAccessDisplayState = ({
  state,
  sharedAt,
}: Partial<DataRoomNodeIdWithStateAndSignal>) => {
  if (state !== DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED || !sharedAt)
    return state;

  return !isDateFromLastDays(sharedAt, 5)
    ? DATA_ROOM_NODE_ACCESS_STATES.PENDING
    : state;
};

export const getTopFilesForLeads = <T>(
  dataRoomTopNodes: T[] = [],
  dataRoomStarredNodes: T[] = [],
) =>
  getHasElementsInArray(dataRoomStarredNodes)
    ? dataRoomStarredNodes
    : dataRoomTopNodes;

export const getInitialFileReorderViewMode = (
  fileReorderViewMode: FileReorderViewMode | undefined,
  buyersSort: number,
  files: unknown[],
): FileReorderViewMode => {
  if (fileReorderViewMode) return fileReorderViewMode;

  return buyersSort > MIN_USERS_FOR_LEAD &&
    getArrayLength(files) > MIN_FILES_FOR_LEAD
    ? FILE_REORDER_VIEW_MODE.LEAD
    : FILE_REORDER_VIEW_MODE.FILE;
};

export const getUpdatedAccessLevel = (
  lead: Lead,
  nodeId: string,
): DataRoomNodeAccessLevel =>
  getTopFilesForLeads(lead.dataRoomTopNodes, lead.dataRoomStarredNodes).find(
    ({ dataRoomNodeId }) => dataRoomNodeId === nodeId,
  )?.accessLevel;
